/**
 * @name Acc.jsx
 * @author 김권영
 * @since 2025. 02. 25.
 * @description
 * 사업장(관리자 조회), 공정별 설비의 재해 발생 형태 매핑 관리
 * 공정별 설비 목록 조회, 재해 발생 형태 조회, 선택 후 매핑
 *
 *
 * @param {string} adminCompCode - 관리자 : 선택된 사업장 코드 전역으로 관리
 * @param {string} realCompCode - 관리자 : 선택된 사업장 코드(전역), 유저 : 로그인 정보에 담긴 사업장 코드
 * @param {string} categoryCode - 검색 조건 구분
 */
import React, {useEffect, useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Input, Select, Checkbox, ProcEqptList, CompanySearchModal, SiteSearchModal } from "@components";
import { AuthContext, useAdminCompany, useSiteSelection } from "@context";
import { useAccLogic } from "@hooks";
import { ROLES } from "@constants/roles";
import { get } from "@utils/axios_api";
import { toast } from 'react-toastify';
import "./acci.css";

const Acc = () => {
  // 사용자 권한
  const { auth, hasPermission } = useContext(AuthContext);
  const canWrite = hasPermission('ACCIDENT_WRITE');
  const isAdmin = auth?.role === ROLES.SUPER_ADMIN || auth?.role === ROLES.ADMIN;

  // 전역 선택한 회사 번호 (관리자 - R0/R1 공통)
  const { adminCompCode, adminCompName } = useAdminCompany();

  // 재해관리 공통 회사/사업장 컨텍스트
  const {
      compCode: acciCompCode,
      compName: acciCompName,
      siteCode: acciSiteCode,
      siteName: acciSiteName,
      setSiteSelection: setAcciCompany,
      clearSiteSelection: clearAcciCompany,
  } = useSiteSelection();

  // 일반 사용자의 회사 정보
  const [userSiteList, setUserSiteList] = useState([]);
  const [selectedUserSite, setSelectedUserSite] = useState(acciSiteCode || "");

  const [selectedSite, setSelectedSite] = useState(
      acciSiteCode ? { siteCode: acciSiteCode, siteName: acciSiteName } : null
  );
  const [companyModalOpen, setCompanyModalOpen] = useState(false);
  const [siteModalOpen, setSiteModalOpen] = useState(false);

  const methods = useForm({
    defaultValues: {
      categoryCode: "",
    },
  });

  // 폼 값 추적 (사업장 번호)
  const categoryCode = methods.watch("categoryCode");

  const currentSiteCode = isAdmin ? selectedSite?.siteCode : selectedUserSite;
  const realCompCode = isAdmin ? adminCompCode : auth?.compCode;

  const {
    company, centerAccidentList, rightAccidentList, seriousAccidentByCategory,
    selectedProcEqpt,
    setSelectedProcEqpt, setCenterAccidentList, setRightAccidentList,
    fetchInitialData, fetchCenterAccidentList, fetchRightAccidentList,
    handleSaveAll, handleCenterRowClick, handleRightRowClick,
    moveRightToCenter, moveCenterToRight,
  } = useAccLogic(realCompCode);

  // 일반 사용자 사업장 목록 조회
  useEffect(() => {
    if (!isAdmin && auth?.compCode) {
      const fetchUserSites = async () => {
        try {
          const data = await get("/user/procEqptInit", {
            params: { compCode: auth.compCode },
          });
          if (data.siteList && data.siteList.length > 0) {
            setUserSiteList(data.siteList);
            const contextInList = acciSiteCode && data.siteList.some(s => s.siteCode === acciSiteCode);
            if (!contextInList) {
              const firstCode = data.siteList[0].siteCode;
              const firstName = data.siteList[0].siteName;
              setSelectedUserSite(firstCode);
              setAcciCompany(auth.compCode, auth.compName || "", firstCode, firstName);
            }
          }
        } catch (err) {
          console.error("사업장 목록 조회 오류:", err);
        }
      };
      fetchUserSites();
    }
  }, [isAdmin, auth?.compCode]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (realCompCode) {
      fetchInitialData(realCompCode);
    }
  }, [realCompCode, fetchInitialData]);


  // 공정목록 클릭 시
  const handleClickProcessRow = async (item) => {
    setSelectedProcEqpt(item.mpreqCode);
    setRightAccidentList([]);
    await fetchCenterAccidentList(realCompCode, item.mpreqCode);
  };

  // 오른쪽 끝 테이블 조회
  const handleSearchAccident = async () => {
    if (!selectedProcEqpt) {
      toast.warn("공정(설비)를 먼저 선택해주세요.");
      return;
    }
    await fetchRightAccidentList({
      compCodeParam: realCompCode,
      mpreqCodeParam: selectedProcEqpt,
      categoryCodeParam: categoryCode,
    });
  };

  const onSaveAll = async () => {
    await handleSaveAll(realCompCode, selectedProcEqpt, categoryCode);
  };

  // 모달에서 선택한 사업장 정보
  const handleModalSuccess = (selectedCompnay) => {
    // 필요한 경우 추가 로직 구현
  };

  const showMainContent = isAdmin ? Boolean(currentSiteCode) : true;

  return (
      <FormProvider {...methods}>
        <form>
          <div className="contents-wrap">
            <div className="page-title-wrap">
              <h2>
                <span className="icon-dot-circle"></span>발생가능 형태 - 공정·설비·재해발생형태
              </h2>
            </div>

            <div className="search-wrap">
              <div className="search-row">
                <div className="search-item">
                  {isAdmin ? (
                      // 관리자(R0/R1): 회사 Input + 모달 버튼
                      <>
                        <label>회사</label>
                        <Input
                            name="companyName"
                            disabled
                            value={adminCompName || ''}
                        />
                        <button
                            type="button"
                            onClick={() => setCompanyModalOpen(true)}
                            className="search-icon"
                        />
                      </>
                  ) : (
                      // 일반 사용자용: 사업장 선택만
                      <>
                        <label>사업장</label>
                        <select
                            value={selectedUserSite}
                            onChange={(e) => {
                              const siteCode = e.target.value;
                              setSelectedUserSite(siteCode);
                              const siteName = userSiteList.find(s => s.siteCode === siteCode)?.siteName || "";
                              setAcciCompany(auth.compCode, auth.compName || "", siteCode, siteName);
                            }}
                            style={{
                              padding: '8px 12px',
                              width: '200px',
                              border: '1px solid #ddd',
                              borderRadius: '4px'
                            }}
                        >
                          <option value="">사업장을 선택하세요</option>
                          {userSiteList.map(site => (
                              <option key={site.siteCode} value={site.siteCode}>
                                {site.siteName}
                              </option>
                          ))}
                        </select>
                      </>
                  )}
                </div>

                {isAdmin && (
                    <div className="search-item">
                      <label>사업장</label>
                      <Input
                          name="siteName"
                          disabled
                          value={selectedSite?.siteName || ''}
                          placeholder={adminCompCode ? "사업장을 선택하세요" : "회사를 먼저 선택하세요"}
                      />
                      {adminCompCode && (
                          <button
                              type="button"
                              onClick={() => setSiteModalOpen(true)}
                              className="search-icon"
                          />
                      )}
                    </div>
                )}
                <div className="search-btn-container">
                  <div className="btn-right-box">
                    <ul>
                      {canWrite && <li><button type="button" title="저장" className="btn-save" onClick={onSaveAll}>저장</button></li>}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* 전체 레이아웃 (수평으로 N개 구역) - 사업장이 선택된 경우에만 표시 */}
            {showMainContent ? (
                <div className="total-layout">

                  {/* --- 1) 공정/설비 목록 테이블 --- */}
                  <ProcEqptList
                      compCode={realCompCode}
                      siteCode={currentSiteCode}
                      selectedProcEqpt={selectedProcEqpt}
                      onRowClick={handleClickProcessRow}
                  />

                  {/* --- 2) 재해 발생 형태 --- */}
                  <section className="section-style">
                    <div className="section-tit-wrap" style={{height:"5%"}}>
                      <h3 className="border-left-primary"> &nbsp;재해 발생 형태</h3>
                    </div>

                    <div className="acc-table-wrap">
                      <table className="grid-list acc-list">
                        <thead>
                        <tr>
                          <th style={{ width: "50px" }}></th>
                          <th style={{ width: "200px"}}>구분</th>
                          <th style={{ width: "200px" }}>발생형태</th>
                          <th style={{ width: "80px" }}>중대재해</th>
                        </tr>
                        </thead>
                        <tbody>
                        {centerAccidentList.map((row) => (
                            <tr
                                key={row.saccCode}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleCenterRowClick(row.saccCode)}
                            >
                              <td>
                                <Checkbox type="checkbox"
                                          name="saccCode"
                                          readOnly
                                          checked={row.isChecked ?? false}
                                />
                              </td>
                              <td style={{textAlign:"left"}}>{row.categoryName}</td>
                              <td style={{textAlign:"left"}}>{row.saccName}</td>
                              <td>
                                <input
                                    type="checkbox"
                                    checked={row.saccYn === 1}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setCenterAccidentList((prev) =>
                                          prev.map((r) => {
                                            if (r.saccCode === row.saccCode) {
                                              return {
                                                ...r,
                                                saccYn: checked ? 1 : 0,
                                              };
                                            }
                                            return r;
                                          })
                                      );
                                    }}
                                />
                              </td>
                            </tr>
                        ))}
                        </tbody>
                      </table>
                    </div>
                  </section>

                  {/* --- 3) 화살표 버튼 (가운데 <-> 오른쪽) --- */}
                  <div className="arrow-btn-wrap">
                    {/* 오른쪽 → 가운데 */}
                    <button type="button" className="btn-add arrow-btn" onClick={moveRightToCenter}>◀</button>
                    {/* 가운데 → 오른쪽 */}
                    <button type="button" className="btn-add arrow-btn" onClick={moveCenterToRight}>▶</button>
                  </div>

                  {/* --- 4) 오른쪽 테이블(재해발생형태목록) --- */}
                  <section className="section-style">
                    <div className="section-tit-wrap" style={{height:"5%"}}>
                      <h3 className="border-left-primary">&nbsp;재해발생형태 목록 </h3>
                    </div>
                    <div className="list-search-wrap" style={{height:"5%"}}>
                      <div className="list-search-item">
                        <>
                          <label>구분</label>
                          <Select
                              name="categoryCode"
                              options={seriousAccidentByCategory}
                              style={{ width:"300px", maxWidth:"300px"}}
                              defaultOption="선택"
                              className="input-select"
                              valueKey="categoryCode" // key 값
                              labelKey="categoryName" // 텍스트 보이는 이름
                          />
                        </>
                      </div>
                      <div className="list-search-container">
                        <button type="button" className="list-btn-search btn-add" onClick={handleSearchAccident}>조회</button>
                      </div>
                    </div>

                    <div className="acc-table-wrap" style={{height: "90%"}} >
                      <table className="grid-list acc-list" >
                        <thead>
                        <tr>
                          <th style={{ width: "25px" }}></th>
                          {/* <th style={{ width: "260px" }}>구분</th> */}
                          <th style={{ minWidth: "100px" }}>발생형태</th>
                        </tr>
                        </thead>
                        <tbody>
                        {rightAccidentList.map((row) => (
                            <tr
                                key={row.saccCode}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleRightRowClick(row.saccCode)}
                            >
                              <td>
                                <Checkbox type="checkbox"
                                          name="saccCode"
                                          readOnly
                                          checked={row.isChecked ?? false}
                                />
                              </td>
                              {/* <td>{row.categoryName}</td> */}
                              <td style={{textAlign:"left"}}>{row.saccName}</td>
                            </tr>
                        ))}

                        {/* 데이터 없을 때 표시 */}
                        {rightAccidentList.length === 0 && (
                            <tr>
                              <td colSpan={3} style={{ textAlign: "center" }}>
                                구분을 조회 하세요.
                              </td>
                            </tr>
                        )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </div>
            ) : (
                <div style={{ textAlign: 'center', padding: '50px', fontSize: '16px', color: '#666' }}>
                  위에서 회사와 사업장을 선택하세요.
                </div>
            )}
          </div>
        </form>

        {/* 회사 선택 모달 */}
        {isAdmin && (
            <CompanySearchModal
                isOpen={companyModalOpen}
                onClose={() => setCompanyModalOpen(false)}
                onConfirm={() => {
                  setSelectedSite(null);
                  clearAcciCompany();
                  setCompanyModalOpen(false);
                }}
            />
        )}

        {/* 사업장 선택 모달 */}
        {isAdmin && (
            <SiteSearchModal
                isOpen={siteModalOpen}
                onClose={() => setSiteModalOpen(false)}
                compCode={adminCompCode}
                onConfirm={(site) => {
                  setSelectedSite(site);
                  setAcciCompany(adminCompCode, adminCompName, site.siteCode, site.siteName);
                  setSiteModalOpen(false);
                }}
            />
        )}
      </FormProvider>
  );
};

export { Acc };